/**
 * Contract status IDs — must stay aligned with libs/models contract-status.ts and the API.
 * Defined locally so utils does not depend on @marina/models at build time (tsc rootDir).
 */
const ContractStatus = {
  Contracted: 4,
  PendingTermination: 5,
  Terminated: 6,
} as const;

/** Minimal lease fields needed to decide service access (e.g. launch requests). */
export interface LeaseServiceAccessInput {
  status: number;
  endDate?: string | null;
  terminationRequestedDate?: string | null;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const dateOnly = String(value).substring(0, 10);
  if (!dateOnly) return null;
  const parsed = new Date(`${dateOnly}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whether the client may use marina services (e.g. launch requests) for this contract as of `asOf`.
 * Contracted and notice-period (pending/approved termination before leave date) contracts qualify.
 */
export function isContractActiveForServices(
  contract: LeaseServiceAccessInput,
  asOf: Date = new Date()
): boolean {
  const today = startOfDay(asOf);
  const endDate = parseDateOnly(contract.endDate);
  if (endDate && endDate < today) {
    return false;
  }

  const leaveDate =
    endDate ?? parseDateOnly(contract.terminationRequestedDate);

  switch (contract.status) {
    case ContractStatus.Contracted:
      return true;
    case ContractStatus.PendingTermination:
      return !leaveDate || leaveDate >= today;
    case ContractStatus.Terminated:
      return !!leaveDate && leaveDate >= today;
    default:
      return false;
  }
}

/** True when any lease for a vessel grants service access. */
export function vesselHasActiveContractForServices(
  contracts: LeaseServiceAccessInput[],
  asOf: Date = new Date()
): boolean {
  return contracts.some((c) => isContractActiveForServices(c, asOf));
}

/** Normalize GUIDs for Set/Map comparisons (API may return mixed casing). */
export function normalizeGuid(id: string | null | undefined): string {
  return (id || '').trim().toLowerCase();
}

const EmptyGuid = '00000000-0000-0000-0000-000000000000';

/** Whether a lease should display as occupying a dry-storage slot on the rack map. */
export function isLeaseOccupyingDryStorageSlot(
  status: number | null | undefined,
  endDate?: string | null
): boolean {
  if (status == null) {
    return false;
  }
  if (status === ContractStatus.Contracted || status === ContractStatus.PendingTermination) {
    return true;
  }
  if (status === ContractStatus.Terminated && endDate) {
    const today = startOfDay(new Date());
    const leave = parseDateOnly(endDate);
    return !!leave && leave >= today;
  }
  return false;
}

/** True when a lease is tied to the given dry-storage slot id. */
export function leaseMatchesDryDockSlot(
  lease: { dryDockLocationId?: string | null; slipId?: string | null },
  slotId: string
): boolean {
  const slot = normalizeGuid(slotId);
  if (!slot) {
    return false;
  }
  const locationId = normalizeGuid(lease.dryDockLocationId);
  if (locationId && locationId === slot) {
    return true;
  }
  const slip = normalizeGuid(lease.slipId);
  return !!slip && slip !== EmptyGuid && slip === slot;
}
